// src/components/NavBar.js
/* ------------------------------------------------------------------ */
/* NavBar – responsive top-level navigation                            */
/* ------------------------------------------------------------------ */
/**
 * Behaviour
 * ----------
 * • **Mobile (< md)** – hamburger toggles a slide-down, full-height overlay
 *   containing links: Home · Assessment · Questionnaires · Results · Sign out.
 * • **Desktop (≥ md)** – keeps inline links and icons.
 *
 * @param {object}  props
 * @param {string=} props.className  Extra Tailwind classes for the `<header>`.
 * @returns {JSX.Element}
 */

import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { firebaseApp } from "../../firebase/firebaseApp";
import { HomeIcon } from "../icons/HomeIcon";
import { LogoutIcon } from "../icons/LogoutIcon";
import { CloseIcon } from "../icons/CloseIcon";
import { HamburgerIcon } from "../icons/HamburgerIcon";

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
function NavBar({ className = "" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  /** Sign user out and redirect to `/auth/login` */
  const handleSignOut = useCallback(() => {
    signOut(getAuth(firebaseApp))
      .then(() => navigate("/auth/login", { replace: true }))
      .catch(
        (err) => process.env.NODE_ENV === "development" && console.error(err)
      );
  }, [navigate]);

  /** Close the slide-down menu after a navigation click */
  const handleNavigate = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <header
        className={`flex items-center justify-between w-full p-2 md:gap-4 z-50 ${className}`}
      >
        {/* Logo */}
        <Link to="/home" aria-label="PDX Home" onClick={handleNavigate}>
          <img
            src="/pdx_logo.png"
            alt="PDX logo"
            className="h-12 w-auto"
            loading="lazy"
          />
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/cohort-overview" className="nav-title">
            Cohort Overview
          </Link>
          <Link to="/questionnaires" className="nav-title">
            Questionnaires
          </Link>
          {/* <Link to="/results" className="nav-title">
            Results
          </Link> */}
          <Link to="/recording" className="nav-title">
            Assessment
          </Link>
        </nav>

        {/* Desktop icons */}
        <nav className="hidden md:flex items-center gap-4">
          <Link to="/home" aria-label="Home">
            <HomeIcon />
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Sign out"
            className="focus:outline-none"
          >
            <LogoutIcon />
          </button>
        </nav>

        {/* Hamburger (mobile) */}
        <button
          className="md:hidden focus:outline-none"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </button>
      </header>

      {/* ── Slide-down overlay (mobile) ─────────────────────────────── */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-white flex flex-col items-start
                     py-24 p-8 gap-8 animate-[slideDown_0.25s_ease-out]"
        >
          <Link
            to="/home"
            onClick={handleNavigate}
            className="subtitle-text hover:underline"
          >
            Home
          </Link>
          <Link
            to="/recording"
            onClick={handleNavigate}
            className="subtitle-text hover:underline"
          >
            Assessment
          </Link>
          <Link
            to="/questionnaires"
            onClick={handleNavigate}
            className="subtitle-text hover:underline"
          >
            Questionnaires
          </Link>
          {/* <Link
            to="/results"
            onClick={handleNavigate}
            className="subtitle-text hover:underline"
          >
            Results
          </Link> */}
          <button
            type="button"
            onClick={() => {
              handleSignOut();
              setMenuOpen(false);
            }}
            className="subtitle-text hover:underline"
          >
            Sign out
          </button>
        </div>
      )}

      {/* ── Slide-down keyframe ────────────────────────────────────── */}
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

export default React.memo(NavBar);
